/**
 * Dynamically loads a script into the document body by src and optional id.
 * Prevents duplicate insertion when an element with the same id exists.
 * Returns a disposer function to remove the script when no longer needed.
 *
 * @param {string} src - Public path to the script (e.g., '/assets/js/order-submitted.js').
 * @param {string} id - Unique DOM id for the script element to prevent duplicates.
 * @returns {() => void} cleanup function to remove the script element.
 */
export default (src, id) => {
  if (!src) return () => {};

  if (id) {
    const existingById = document.getElementById(id);
    if (existingById) {
      return () => {
        const stillExisting = document.getElementById(id);
        // #genai: use optional chaining and Node.remove() instead of parentNode.removeChild (Sonar S6582/S7762); behavior unchanged
        stillExisting?.remove();
      };
    }
  }

  const script = document.createElement('script');
  if (id) script.id = id;
  script.src = src;
  script.async = true;
  document.body.appendChild(script);

  return () => {
    if (id) {
      const existing = document.getElementById(id);
      // #genai: use optional chaining and Node.remove() instead of parentNode.removeChild (Sonar S6582/S7762); behavior unchanged
      existing?.remove();
      return;
    }
    // #genai Node.remove() is a no-op without a parent, matching the previous parentNode guard.
    script?.remove();
  };
};
